import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Calendar, CheckCircle2, XCircle, Users, Trash, Trash2 } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AttendancePage({ user }) {
  const [disciples, setDisciples] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [attendanceType, setAttendanceType] = useState("homecell");
  const [selectedDisciples, setSelectedDisciples] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDisciples();
    fetchAttendance();
    if (user.role === 'super_admin') {
      fetchLeaders();
    }
  }, []);

  const fetchDisciples = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/disciples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisciples(response.data);
    } catch (error) {
      toast.error("Failed to fetch disciples");
    }
  };

  const fetchLeaders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaders(response.data.filter(u => u.role === 'homecell_leader'));
    } catch (error) {
      console.error("Failed to fetch leaders");
    }
  };

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendance(response.data);
    } catch (error) {
      toast.error("Failed to fetch attendance");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/attendance/bulk`,
        {
          disciple_ids: selectedDisciples,
          attendance_type: attendanceType,
          date: selectedDate,
          present: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Attendance recorded successfully");
      fetchAttendance();
      setDialogOpen(false);
      setSelectedDisciples([]);
    } catch (error) {
      toast.error("Failed to record attendance");
    } finally {
      setLoading(false);
    }
  };

  const toggleDisciple = (id) => {
    setSelectedDisciples((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleResetAttendance = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL attendance records for ALL disciples and leaders. This action cannot be undone. Are you absolutely sure?")) {
      return;
    }

    // Double confirmation
    if (!window.confirm("This is your final confirmation. All attendance data will be lost. Continue?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/attendance/reset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset attendance");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selectedDisciples.length === disciples.length) {
      setSelectedDisciples([]);
    } else {
      setSelectedDisciples(disciples.map((d) => d.id));
    }
  };

  const canEditAttendance = (record) =>
    user.role === 'super_admin' ||
    (user.role === 'homecell_leader' && record.homecell_leader_id === user.id);

  const handleToggleAttendance = async (record) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/attendance/${record.id}`,
        { present: !record.present },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked as ${!record.present ? "present" : "absent"}`);
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update attendance");
    }
  };

  const handleDeleteAttendance = async (record) => {
    if (!window.confirm("Delete this attendance record?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/attendance/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Attendance record deleted");
      fetchAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete attendance");
    }
  };

  const getAttendanceForDate = (date, type) => {
    return attendance.filter(
      (a) => a.date === date && a.attendance_type === type
    );
  };

  const recentDates = [...new Set(attendance.map((a) => a.date))]
    .sort()
    .reverse()
    .slice(0, 10);

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="gradient-text">Attendance</span>
          </h1>
          <p className="text-gray-600">Track homecell and church service attendance</p>
        </div>
        {user.role === 'super_admin' && (
          <Button 
            onClick={handleResetAttendance}
            variant="outline"
            className="border-red-500 text-red-600 hover:bg-red-50"
            disabled={loading}
          >
            <Trash className="mr-2 h-4 w-4" />
            Reset All Attendance
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Attendance Records</p>
              <p className="text-3xl font-bold" data-testid="total-attendance">{attendance.length}</p>
            </div>
            <Calendar className="h-12 w-12 text-white/40" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Present Today</p>
              <p className="text-3xl font-bold text-gray-800">
                {attendance.filter((a) => a.date === format(new Date(), "yyyy-MM-dd") && a.present).length}
              </p>
            </div>
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="mark-attendance-button" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                <Plus className="mr-2 h-4 w-4" /> Mark Attendance
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Mark Attendance</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date</Label>
                    <input
                      id="date"
                      data-testid="attendance-date"
                      type="date"
                      className="w-full p-2 border rounded-lg"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <select
                      id="type"
                      data-testid="attendance-type"
                      className="w-full p-2 border rounded-lg"
                      value={attendanceType}
                      onChange={(e) => setAttendanceType(e.target.value)}
                    >
                      <option value="homecell">Homecell Meeting</option>
                      <option value="church_service">Church Service</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Select Disciples</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={toggleAll}
                      data-testid="select-all-disciples"
                    >
                      {selectedDisciples.length === disciples.length ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                    {user.role === 'super_admin' ? (
                      // Group by leader for super admin
                      leaders.map(leader => {
                        const leaderDisciples = disciples.filter(d => d.homecell_leader_id === leader.id);
                        if (leaderDisciples.length === 0) return null;
                        
                        return (
                          <div key={leader.id} className="mb-4">
                            <div className="font-semibold text-sm text-purple-700 mb-2 flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              {leader.homecell_name || leader.name}&apos;s Homecell
                            </div>
                            <div className="space-y-2 pl-6">
                              {leaderDisciples.map((disciple) => (
                                <div key={disciple.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    data-testid={`attendance-checkbox-${disciple.id}`}
                                    checked={selectedDisciples.includes(disciple.id)}
                                    onCheckedChange={() => toggleDisciple(disciple.id)}
                                  />
                                  <label className="flex-1 cursor-pointer" onClick={() => toggleDisciple(disciple.id)}>
                                    {disciple.name} {disciple.surname} (Gen {disciple.generation})
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      // Display for homecell leaders with their homecell name
                      <div>
                        <div className="font-semibold text-sm text-purple-700 mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {user.homecell_name || user.name}&apos;s Homecell
                        </div>
                        <div className="space-y-2 pl-6">
                          {disciples.map((disciple) => (
                            <div key={disciple.id} className="flex items-center space-x-2">
                              <Checkbox
                                data-testid={`attendance-checkbox-${disciple.id}`}
                                checked={selectedDisciples.includes(disciple.id)}
                                onCheckedChange={() => toggleDisciple(disciple.id)}
                              />
                              <label className="flex-1 cursor-pointer" onClick={() => toggleDisciple(disciple.id)}>
                                {disciple.name} {disciple.surname} (Gen {disciple.generation})
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="save-attendance-button" type="submit" disabled={loading || selectedDisciples.length === 0} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    {loading ? "Saving..." : "Save Attendance"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4">Recent Attendance</h2>
        <div className="space-y-6">
          {recentDates.map((date) => {
            // Get all attendance for this date
            const dateAttendance = attendance.filter((a) => a.date === date);
            
            // Group disciples by homecell leader
            const leaderGroups = {};
            
            if (user.role === 'super_admin') {
              // For super admin, group by all leaders
              leaders.forEach(leader => {
                const leaderDisciples = disciples.filter(d => d.homecell_leader_id === leader.id);
                if (leaderDisciples.length > 0) {
                  leaderGroups[leader.id] = {
                    leader: leader,
                    disciples: leaderDisciples
                  };
                }
              });
            } else {
              // For homecell leader, only show their own disciples
              leaderGroups[user.id] = {
                leader: user,
                disciples: disciples
              };
            }
            
            return (
              <div key={date} className="border-b pb-4 last:border-b-0">
                <h3 className="font-semibold text-lg mb-4 text-purple-700">
                  {format(new Date(date), "MMMM d, yyyy")}
                </h3>
                
                {Object.entries(leaderGroups).map(([leaderId, group]) => {
                  // Get disciples who have attendance on this date
                  const disciplesWithAttendance = group.disciples.filter(d => 
                    dateAttendance.some(a => a.disciple_id === d.id)
                  );
                  
                  if (disciplesWithAttendance.length === 0) return null;
                  
                  return (
                    <div key={leaderId} className="mb-4 ml-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-purple-600" />
                        <span className="font-semibold text-purple-700">
                          {group.leader.name}
                        </span>
                        <span className="text-sm text-gray-600">
                          ({group.leader.homecell_name || "Homecell"})
                        </span>
                      </div>
                      
                      <div className="ml-6 space-y-2">
                        {disciplesWithAttendance.map((disciple) => {
                          const homecellRecord = dateAttendance.find(
                            (a) => a.disciple_id === disciple.id && a.attendance_type === "homecell"
                          );
                          const churchRecord = dateAttendance.find(
                            (a) => a.disciple_id === disciple.id && a.attendance_type === "church_service"
                          );
                          
                          return (
                            <div key={disciple.id} className="flex items-center gap-4 text-sm bg-gray-50 p-2 rounded">
                              <span className="font-medium min-w-[150px]">
                                {disciple.name} {disciple.surname}
                              </span>
                              
                              <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-600 font-medium min-w-[80px]">Homecell:</span>
                                  {homecellRecord ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        data-testid={`toggle-homecell-${disciple.id}-${date}`}
                                        onClick={() => canEditAttendance(homecellRecord) && handleToggleAttendance(homecellRecord)}
                                        disabled={!canEditAttendance(homecellRecord)}
                                        title={canEditAttendance(homecellRecord) ? "Click to toggle present/absent" : ""}
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded transition ${
                                          homecellRecord.present ? "text-green-700 hover:bg-green-100" : "text-red-700 hover:bg-red-100"
                                        } ${canEditAttendance(homecellRecord) ? "cursor-pointer" : "cursor-default"}`}
                                      >
                                        {homecellRecord.present ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                        <span className="text-xs font-medium">{homecellRecord.present ? "Present" : "Absent"}</span>
                                      </button>
                                      {canEditAttendance(homecellRecord) && (
                                        <button
                                          type="button"
                                          data-testid={`delete-homecell-${disciple.id}-${date}`}
                                          onClick={() => handleDeleteAttendance(homecellRecord)}
                                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                                          title="Delete record"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">No record</span>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-600 font-medium min-w-[80px]">Church:</span>
                                  {churchRecord ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        data-testid={`toggle-church-${disciple.id}-${date}`}
                                        onClick={() => canEditAttendance(churchRecord) && handleToggleAttendance(churchRecord)}
                                        disabled={!canEditAttendance(churchRecord)}
                                        title={canEditAttendance(churchRecord) ? "Click to toggle present/absent" : ""}
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded transition ${
                                          churchRecord.present ? "text-green-700 hover:bg-green-100" : "text-red-700 hover:bg-red-100"
                                        } ${canEditAttendance(churchRecord) ? "cursor-pointer" : "cursor-default"}`}
                                      >
                                        {churchRecord.present ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                        <span className="text-xs font-medium">{churchRecord.present ? "Present" : "Absent"}</span>
                                      </button>
                                      {canEditAttendance(churchRecord) && (
                                        <button
                                          type="button"
                                          data-testid={`delete-church-${disciple.id}-${date}`}
                                          onClick={() => handleDeleteAttendance(churchRecord)}
                                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                                          title="Delete record"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">No record</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                
                {Object.values(leaderGroups).every(group => 
                  group.disciples.filter(d => dateAttendance.some(a => a.disciple_id === d.id)).length === 0
                ) && (
                  <p className="text-sm text-gray-400 ml-4">No attendance records for this date</p>
                )}
              </div>
            );
          })}

          {recentDates.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No attendance records yet. Start tracking attendance!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}